import React from 'react'
import { motion } from 'framer-motion'

interface EnhancedLoadingSpinnerProps {
  title: string
  subtitle: string
  icons: React.ReactNode[]
  showProgressBar?: boolean
  showCircularProgress?: boolean
}

const EnhancedLoadingSpinner: React.FC<EnhancedLoadingSpinnerProps> = ({
  title,
  subtitle,
  icons,
  showProgressBar = false,
  showCircularProgress = false
}) => {
  return (
    <div className="min-h-screen bg-background-950 flex items-center justify-center">
      <div className="text-center">
        {/* Animated loading spinner */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          className="w-16 h-16 border-4 border-primary-500/20 border-t-primary-500 rounded-full mx-auto mb-6"
        />
        
        {/* Animated icons */}
        <div className="flex justify-center space-x-4 mb-6">
          {icons.map((icon, index) => (
            <motion.div
              key={index}
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity, 
                ease: 'easeInOut',
                delay: index * 0.3
              }}
            >
              {icon}
            </motion.div>
          ))}
        </div>

        {/* Loading text with animation */}
        <motion.h1 
          className="text-3xl font-bold text-background-100 mb-4"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          {title}
        </motion.h1>
        
        <motion.p 
          className="text-background-400 text-lg mb-8"
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          {subtitle}
        </motion.p>

        {/* Optional progress bar */}
        {showProgressBar && (
          <div className="w-64 mx-auto mb-6">
            <div className="bg-background-800 rounded-full h-2 overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-primary-500 to-primary-400"
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              />
            </div>
          </div>
        )}

        {/* Optional circular progress */}
        {showCircularProgress && (
          <div className="relative w-24 h-24 mx-auto mb-6">
            <motion.div
              className="absolute inset-0 border-4 border-primary-500/20 rounded-full"
            />
            <motion.div
              className="absolute inset-0 border-4 border-transparent border-t-primary-500 rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                className="w-3 h-3 bg-primary-500 rounded-full"
                animate={{ scale: [1, 1.5, 1] }}
                transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }}
              />
            </div>
          </div>
        )}

        {/* Animated progress dots */}
        <div className="flex justify-center space-x-2">
          {[0, 1, 2, 3].map((index) => (
            <motion.div
              key={index}
              className="w-3 h-3 bg-primary-500 rounded-full"
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.3, 1, 0.3]
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: index * 0.2
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default EnhancedLoadingSpinner

